// @ts-nocheck

import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { Avatar } from "@/components/ui/Avatar";
import { VerifiedBadge } from "@/components/ui/VerifiedBadge";
import { PostCard } from "@/components/post/PostCard";
import { cn } from "@/lib/utils/cn";
import { formatCount, formatJoinDate } from "@/lib/utils/format";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useInView } from "react-intersection-observer";
import { useEffect } from "react";
import type { Post } from "@/types";

interface DashboardProfile {
  id: string; username: string; display_name: string;
  avatar_url: string | null; bio: string | null;
  is_verified: boolean; karma_score: number;
  post_count: number; comment_count: number;
  likes_given_count: number; likes_received_count: number;
  follower_count: number; following_count: number;
  joined_at: string;
}

const tabs = [
  { id: "posts", label: "My Posts" },
  { id: "bookmarks", label: "Bookmarks" },
  { id: "comments", label: "Comments" },
  { id: "likes", label: "Liked Posts" },
] as const;

type Tab = typeof tabs[number]["id"];

export function DashboardClient({ profile }: { profile: DashboardProfile }) {
  const [activeTab, setActiveTab] = useState<Tab>("posts");

  return (
    <div className="max-w-3xl mx-auto">
      {/* Profile header */}
      <div className="card p-6 mb-5">
        <div className="flex items-start gap-4">
          <Avatar src={profile.avatar_url} alt={profile.display_name} size="xl" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-xl font-bold text-foreground">{profile.display_name}</h1>
              {profile.is_verified && <VerifiedBadge size="sm" />}
            </div>
            <p className="text-sm text-text-muted">@{profile.username}</p>
            {profile.bio && <p className="text-sm text-text-muted mt-2">{profile.bio}</p>}
            <p className="text-xs text-text-subtle mt-2">Member since {formatJoinDate(profile.joined_at)}</p>
          </div>
          <Link to="/settings/profile" className="btn-secondary btn-sm flex-shrink-0">
            Edit profile
          </Link>
        </div>

        {/* Stats grid */}
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-3 mt-5 pt-5 border-t border-border">
          {[
            { label: "Karma", value: profile.karma_score },
            { label: "Posts", value: profile.post_count },
            { label: "Comments", value: profile.comment_count },
            { label: "Likes given", value: profile.likes_given_count },
            { label: "Followers", value: profile.follower_count },
            { label: "Following", value: profile.following_count },
          ].map((stat) => (
            <div key={stat.label} className="text-center">
              <p className="stat-value text-lg">{formatCount(stat.value)}</p>
              <p className="stat-label">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-surface-overlay rounded-2xl p-1 border border-border mb-4">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={cn(
              "flex-1 py-2 px-3 rounded-xl text-sm font-medium transition-all duration-150 relative",
              activeTab === tab.id ? "text-foreground" : "text-text-muted hover:text-foreground"
            )}
          >
            {activeTab === tab.id && (
              <motion.div
                layoutId="dashboard-tab-bg"
                className="absolute inset-0 bg-surface-elevated rounded-xl shadow-sm border border-border"
                transition={{ type: "spring", damping: 30, stiffness: 400 }}
              />
            )}
            <span className="relative z-10">{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Tab content */}
      <AnimatePresence mode="wait">
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          {activeTab === "posts" && <DashboardPosts userId={profile.id} />}
          {activeTab === "bookmarks" && <DashboardBookmarks userId={profile.id} />}
          {activeTab === "comments" && <DashboardComments userId={profile.id} />}
          {activeTab === "likes" && <DashboardLikes userId={profile.id} />}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

function DashboardPosts({ userId }: { userId: string }) {
  return <PostList endpoint={`/api/users/${userId}/posts`} userId={userId} emptyMessage="You haven't posted anything yet." emptyAction={{ href: "/post/create", label: "Create your first post" }} allowDelete />;
}

function DashboardBookmarks({ userId }: { userId: string }) {
  return <PostList endpoint={`/api/users/${userId}/bookmarks`} userId={userId} emptyMessage="No bookmarks yet." emptyAction={{ href: "/feed", label: "Browse the feed" }} />;
}

function DashboardLikes({ userId }: { userId: string }) {
  return <PostList endpoint={`/api/users/${userId}/liked-posts`} userId={userId} emptyMessage="No liked posts yet." />;
}

function DashboardComments({ userId }: { userId: string }) {
  const [comments, setComments] = useState<Array<{ id: string; content: string; post_title: string; post_id: string; post_slug: string; vote_score: number; created_at: string }>>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`/api/users/${userId}/comments`)
      .then(r => r.json())
      .then(d => setComments(d.comments || []))
      .finally(() => setLoading(false));
  }, [userId]);

  if (loading) return <div className="space-y-3">{Array.from({ length: 4 }).map((_, i) => <div key={i} className="skeleton h-20 rounded-2xl" />)}</div>;

  if (!comments.length) return (
    <div className="card p-10 text-center">
      <p className="text-text-muted text-sm">You haven't commented yet.</p>
    </div>
  );

  return (
    <div className="space-y-2">
      {comments.map(c => (
        <Link key={c.id} to={`/post/${c.post_id}/${c.post_slug}#comment-${c.id}`} className="card p-4 block hover:shadow-card-hover transition-shadow">
          <p className="text-xs text-text-subtle mb-1">On: <span className="text-brand hover:underline">{c.post_title}</span></p>
          <p className="text-sm text-foreground line-clamp-2">{c.content}</p>
          <div className="flex items-center gap-3 mt-2">
            <span className="text-xs text-text-subtle">{formatCount(c.vote_score)} points</span>
            <span className="text-xs text-text-subtle">{new Date(c.created_at).toLocaleDateString()}</span>
          </div>
        </Link>
      ))}
    </div>
  );
}

function PostList({ endpoint, userId, emptyMessage, emptyAction, allowDelete }: {
  endpoint: string; userId: string;
  emptyMessage: string; emptyAction?: { href: string; label: string };
  allowDelete?: boolean;
}) {
  const { ref, inView } = useInView();

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } = useInfiniteQuery({
    queryKey: ["dashboard-posts", endpoint],
    queryFn: async ({ pageParam }) => {
      const url = pageParam ? `${endpoint}?cursor=${pageParam}` : endpoint;
      const res = await fetch(url);
      return res.json();
    },
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.cursor ?? null,
  });

  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) fetchNextPage();
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

  if (isLoading) return <div className="space-y-3">{Array.from({ length: 3 }).map((_, i) => <div key={i} className="skeleton h-32 rounded-2xl" />)}</div>;

  const posts: Post[] = data?.pages.flatMap(p => p.posts || []) ?? [];

  if (!posts.length) return (
    <div className="card p-10 text-center">
      <p className="text-text-muted text-sm">{emptyMessage}</p>
      {emptyAction && (
        <Link to={emptyAction.href} className="btn-primary btn-sm mt-4 inline-flex">{emptyAction.label}</Link>
      )}
    </div>
  );

  return (
    <div className="space-y-3">
      {posts.map(post => <PostCard key={post.id} post={post} userId={userId} />)}
      <div ref={ref} className="h-4" />
      {isFetchingNextPage && <div className="skeleton h-32 rounded-2xl" />}
    </div>
  );
}
